import { TimeInterval } from "@/types";

/**
 * Backend-stored account default date keys.
 *
 * These map to specific `TimeInterval` presets for UI and report metadata.
 */
export type AccountDefaultDateKey =
  | "last_7_days"
  | "last_14_days"
  | "last_28_days"
  | "last_30_days";

export const DEFAULT_ACCOUNT_DEFAULT_DATE: AccountDefaultDateKey = "last_7_days";

interface DefaultDateOption {
  key: AccountDefaultDateKey;
  label: TimeInterval;
}

export const ACCOUNT_DEFAULT_DATE_OPTIONS: DefaultDateOption[] = [
  { key: "last_7_days", label: TimeInterval.LAST_7_DAYS },
  { key: "last_14_days", label: TimeInterval.LAST_14_DAYS },
  { key: "last_28_days", label: TimeInterval.LAST_28_DAYS },
  { key: "last_30_days", label: TimeInterval.LAST_30_DAYS },
];

const VALID_DEFAULT_DATE_KEYS = new Set<AccountDefaultDateKey>([
  "last_7_days",
  "last_14_days",
  "last_28_days",
  "last_30_days",
]);

/**
 * Normalizes an arbitrary string into a supported `AccountDefaultDateKey`,
 * falling back to `DEFAULT_ACCOUNT_DEFAULT_DATE` when unknown.
 */
export const normalizeAccountDefaultDateKey = (
  value?: string | null,
): AccountDefaultDateKey => {
  if (value && VALID_DEFAULT_DATE_KEYS.has(value as AccountDefaultDateKey)) {
    return value as AccountDefaultDateKey;
  }
  return DEFAULT_ACCOUNT_DEFAULT_DATE;
};

/**
 * Safely reads `additional_info.default_date` from an account-like object.
 * The account shape is intentionally loose to avoid tight coupling.
 */
export const getAccountDefaultDateKeyFromAccount = (
  account?: { additional_info?: { default_date?: string } | null } | null,
): AccountDefaultDateKey => {
  const raw = account?.additional_info?.default_date;
  return normalizeAccountDefaultDateKey(raw);
};

const KEY_TO_INTERVAL: Record<AccountDefaultDateKey, TimeInterval> = {
  last_7_days: TimeInterval.LAST_7_DAYS,
  last_14_days: TimeInterval.LAST_14_DAYS,
  last_28_days: TimeInterval.LAST_28_DAYS,
  last_30_days: TimeInterval.LAST_30_DAYS,
};

export const accountDefaultDateKeyToTimeInterval = (
  key: AccountDefaultDateKey,
): TimeInterval => KEY_TO_INTERVAL[key] ?? TimeInterval.LAST_7_DAYS;

